import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, Upload, Image, Save, Trash2, Eye,
  Building2, Phone, FileText, CheckCircle, MessageCircle, Key, Shield, EyeOff,
  Link2, RefreshCw, AlertCircle, ChevronDown, ChevronUp, ExternalLink,
  Loader2, Check, X, Zap, FileKey, Send, RotateCcw, MessageSquare
} from 'lucide-react';
import { settingsAPI, zatcaAPI, branchesAPI, onboardingAPI } from '../../services/api';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import toast from 'react-hot-toast';

// ====== ZATCA Integration Section Component ======
const ZatcaIntegration = () => {
  // Branch selector
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  const [loading, setLoading] = useState(true);
  const [branchesLoaded, setBranchesLoaded] = useState(false);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const res = await branchesAPI.getAll({ active: true, limit: 100 });
        // Handle various response formats
        let data = res.data?.data || res.data?.branches || res.data;
        if (data && !Array.isArray(data)) {
          // If it's an object with a nested array
          data = Object.values(data).find(v => Array.isArray(v)) || [];
        }
        const branchList = Array.isArray(data) ? data : [];
        setBranches(branchList);
        if (branchList.length > 0) {
          setSelectedBranchId(branchList[0]._id);
        }
      } catch (err) {
        console.error('Failed to load branches:', err);
      }
      setBranchesLoaded(true);
    };
    loadBranches();
  }, []);
  const [step, setStep] = useState(1); // 1: Profile, 2: CSR, 3: OTP, 4: Done
  const [zatcaStatus, setZatcaStatus] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [otp, setOtp] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Profile form
  const [profile, setProfile] = useState({
    vatNumber: '',
    crNumber: '',
    organizationName: '',
    organizationUnitName: 'Main Branch',
    businessCategory: 'Medical laboratories',
    invoiceType: '1100',
    locationAddress: 'RRRD2929'
  });

  // Load ZATCA status when branch changes or branches finish loading
  useEffect(() => {
    if (branchesLoaded) loadStatus();
  }, [selectedBranchId, branchesLoaded]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const response = await zatcaAPI.getStatus(selectedBranchId);
      const data = response.data?.data || response.data;
      setZatcaStatus(data);

      // Set profile from existing data
      if (data.profile) {
        setProfile(prev => ({
          ...prev,
          vatNumber: data.profile.vatNumber || '',
          crNumber: data.profile.crNumber || '',
          organizationName: data.profile.organizationName || '',
          organizationUnitName: data.profile.organizationUnitName || 'Main Branch',
          businessCategory: data.profile.businessCategory || 'Medical laboratories',
          invoiceType: data.profile.invoiceType || '1100',
          locationAddress: data.profile.locationAddress || 'RRRD2929'
        }));
      }

      // Set step based on current status
      switch (data.status) {
        case 'CSR_GENERATED':
          setStep(3); // Ready for OTP
          break;
        case 'COMPLIANCE_DONE':
          setStep(4); // Connected
          break;
        case 'PRODUCTION_READY':
          setStep(4); // Fully done
          break;
        default:
          setStep(data.hasProfile ? 2 : 1);
      }
    } catch (err) {
      console.error('Failed to load ZATCA status:', err);
    }
    setLoading(false);
  };

  // Step 1: Save Profile
  const handleSaveProfile = async () => {
    if (!profile.vatNumber || !profile.crNumber || !profile.organizationName) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    if (!/^3\d{13}3$/.test(profile.vatNumber)) {
      toast.error('الرقم الضريبي يجب أن يكون 15 رقم ويبدأ وينتهي بـ 3');
      return;
    }

    setProcessing(true);
    setLastError(null);
    try {
      await zatcaAPI.saveProfile(profile);
      toast.success('تم حفظ بيانات الشركة');
      setStep(2);
      await loadStatus();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setLastError(msg);
      toast.error(msg);
    }
    setProcessing(false);
  };

  // Step 2: Generate CSR
  const handleGenerateCsr = async () => {
    setProcessing(true);
    setLastError(null);
    try {
      await zatcaAPI.generateCsr(selectedBranchId);
      toast.success('تم توليد طلب الشهادة (CSR) بنجاح');
      setStep(3);
      await loadStatus();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setLastError(msg);
      toast.error(msg);
    }
    setProcessing(false);
  };

  // Step 3: Request Compliance CSID
  const handleRequestCompliance = async () => {
    if (!otp || otp.length < 4) {
      toast.error('يرجى إدخال رمز OTP صالح');
      return;
    }

    setProcessing(true);
    setLastError(null);
    try {
      const response = await zatcaAPI.requestCompliance(otp, selectedBranchId);
      toast.success('🎉 تم الربط مع ZATCA بنجاح!');
      setStep(4);
      setOtp('');
      await loadStatus();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setLastError(msg);
      toast.error(msg);
    }
    setProcessing(false);
  };

  // Run compliance checks (6 invoices)
  const [complianceResults, setComplianceResults] = useState(null);
  const handleRunComplianceChecks = async () => {
    setProcessing(true);
    setLastError(null);
    setComplianceResults(null);
    try {
      const response = await zatcaAPI.runComplianceChecks(selectedBranchId);
      const data = response.data?.data || response.data;
      setComplianceResults(data);
      if (data.success) {
        toast.success('🎉 جميع فحوصات الامتثال نجحت!');
      } else {
        toast.error(`نجح ${data.passed} من ${data.total} فقط`);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setLastError(msg);
      toast.error(msg);
    }
    setProcessing(false);
  };

  // Request Production CSID
  const handleRequestProduction = async () => {
    setProcessing(true);
    setLastError(null);
    try {
      await zatcaAPI.requestProduction(selectedBranchId);
      toast.success(
        zatcaStatus?.environment === 'production'
          ? '🎉 تم الحصول على شهادة الإنتاج النهائية!'
          : '🎉 تم الحصول على Production CSID في البيئة التجريبية!'
      );
      await loadStatus();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setLastError(msg);
      toast.error(msg);
    }
    setProcessing(false);
  };

  // Reset everything
  const handleReset = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة تعيين ربط ZATCA؟ سيتم حذف جميع بيانات الربط.')) {
      return;
    }
    setProcessing(true);
    setLastError(null);
    try {
      await zatcaAPI.reset(selectedBranchId);
      toast.success('تم إعادة التعيين');
      setStep(1);
      setOtp('');
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
    setProcessing(false);
  };

  // Status badge
  const getStatusBadge = () => {
    if (!zatcaStatus) return null;
    const statusMap = {
      'NEW': { label: 'غير متصل', color: 'bg-gray-100 text-gray-600', icon: X },
      'CSR_GENERATED': { label: 'بانتظار OTP', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
      'COMPLIANCE_DONE': { label: 'متصل (تجريبي)', color: 'bg-green-100 text-green-700', icon: Check },
      'PRODUCTION_READY': { label: 'متصل (إنتاج)', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      'FAILED': { label: 'فشل', color: 'bg-red-100 text-red-700', icon: X }
    };
    const s = statusMap[zatcaStatus.status] || statusMap['NEW'];
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${s.color}`}>
        <Icon className="w-4 h-4" />
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const isConnected = zatcaStatus?.status === 'COMPLIANCE_DONE' || zatcaStatus?.status === 'PRODUCTION_READY';
  const isProductionEnv = zatcaStatus?.environment === 'production';

  return (
    <div className="space-y-6">
      {/* Branch Selector */}
      {branches.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <label className="block text-sm font-medium text-blue-800 mb-2">
            <Building2 className="w-4 h-4 inline ml-1" />
            اختر الفرع (كل فرع يُسجل كجهاز منفصل في ZATCA)
          </label>
          <select
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            className="w-full px-4 py-2.5 border border-blue-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            {branches.map(b => (
              <option key={b._id} value={b._id}>{b.branch_name} — {b.city}</option>
            ))}
          </select>
        </div>
      )}

      {/* Header & Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getStatusBadge()}
          {zatcaStatus?.environment && (
            <span className="text-xs text-gray-400">
              البيئة: {zatcaStatus.environment === 'simulation' ? 'تجريبية' : 'إنتاج'}
            </span>
          )}
        </div>
        {zatcaStatus?.status !== 'NEW' && (
          <button
            onClick={handleReset}
            disabled={processing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            إعادة تعيين
          </button>
        )}
      </div>

      {/* Error Display */}
      {lastError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-2"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">حدث خطأ</p>
            <p className="mt-1">{lastError}</p>
          </div>
          <button onClick={() => setLastError(null)} className="mr-auto">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Steps Progress Bar */}
      <div className="flex items-center gap-2">
        {[
          { num: 1, label: 'بيانات الشركة' },
          { num: 2, label: 'توليد CSR' },
          { num: 3, label: 'رمز OTP' },
          { num: 4, label: 'متصل' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-purple-700' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                ${step > s.num ? 'bg-purple-600 text-white' : step === s.num ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-400' : 'bg-gray-100 text-gray-400'}`}>
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span className="text-xs font-medium hidden sm:inline">{s.label}</span>
            </div>
            {i < 3 && <div className={`flex-1 h-0.5 ${step > s.num ? 'bg-purple-500' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* ===== Step 1: Company Profile ===== */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 text-sm text-purple-800">
            <p className="font-medium mb-1">📋 الخطوة الأولى: بيانات الشركة</p>
            <p>أدخل بيانات شركتك كما تظهر في شهادة ضريبة القيمة المضافة والسجل التجاري.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                الرقم الضريبي (VAT) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profile.vatNumber}
                onChange={(e) => setProfile(p => ({ ...p, vatNumber: e.target.value }))}
                placeholder="3XXXXXXXXXXXXX3"
                maxLength={15}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                dir="ltr"
              />
              <p className="text-xs text-gray-400 mt-1">15 رقم، يبدأ وينتهي بـ 3</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                رقم السجل التجاري (CR) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profile.crNumber}
                onChange={(e) => setProfile(p => ({ ...p, crNumber: e.target.value }))}
                placeholder="XXXXXXXXXX"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                dir="ltr"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              اسم الشركة (كما في شهادة الضريبة) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={profile.organizationName}
              onChange={(e) => setProfile(p => ({ ...p, organizationName: e.target.value }))}
              placeholder="مثال: Nukhbat Al Sila Trading Co"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
            <p className="text-xs text-gray-400 mt-1">يجب أن يطابق الاسم في شهادة ضريبة القيمة المضافة تماماً</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">اسم الفرع</label>
              <input
                type="text"
                value={profile.organizationUnitName}
                onChange={(e) => setProfile(p => ({ ...p, organizationUnitName: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">فئة النشاط</label>
              <input
                type="text"
                value={profile.businessCategory}
                onChange={(e) => setProfile(p => ({ ...p, businessCategory: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">نوع الفاتورة</label>
              <select
                value={profile.invoiceType}
                onChange={(e) => setProfile(p => ({ ...p, invoiceType: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 bg-white"
              >
                <option value="1100">B2B + B2C (الكل)</option>
                <option value="1000">B2B فقط (فواتير ضريبية)</option>
                <option value="0100">B2C فقط (فواتير مبسطة)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">العنوان المختصر</label>
              <input
                type="text"
                value={profile.locationAddress}
                onChange={(e) => setProfile(p => ({ ...p, locationAddress: e.target.value }))}
                placeholder="RRRD2929"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                dir="ltr"
              />
            </div>
          </div>

          <button
            onClick={handleSaveProfile}
            disabled={processing}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {processing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            حفظ البيانات والمتابعة
          </button>
        </motion.div>
      )}

      {/* ===== Step 2: Generate CSR ===== */}
      {step === 2 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 text-sm text-blue-800">
            <p className="font-medium mb-1">🔑 الخطوة الثانية: توليد طلب الشهادة</p>
            <p>سيتم توليد طلب شهادة (CSR) باستخدام ZATCA SDK الرسمي بناءً على بياناتك.</p>
          </div>

          {/* Profile Summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <h4 className="font-medium text-gray-700 mb-2">ملخص البيانات:</h4>
            <div className="grid grid-cols-2 gap-2">
              <span className="text-gray-500">الرقم الضريبي:</span>
              <span className="font-mono text-gray-800" dir="ltr">{profile.vatNumber}</span>
              <span className="text-gray-500">السجل التجاري:</span>
              <span className="font-mono text-gray-800" dir="ltr">{profile.crNumber}</span>
              <span className="text-gray-500">اسم الشركة:</span>
              <span className="text-gray-800">{profile.organizationName}</span>
              <span className="text-gray-500">الفرع:</span>
              <span className="text-gray-800">{profile.organizationUnitName}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              تعديل البيانات
            </button>
            <button
              onClick={handleGenerateCsr}
              disabled={processing}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التوليد...
                </>
              ) : (
                <>
                  <FileKey className="w-5 h-5" />
                  توليد CSR
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* ===== Step 3: Enter OTP ===== */}
      {step === 3 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 text-sm text-amber-800">
            <p className="font-medium mb-2">🔐 الخطوة الثالثة: إدخال رمز OTP</p>
            <ol className="list-decimal list-inside space-y-1.5 text-amber-700">
              <li>
                ادخل على بوابة فاتورة
                <a
                  href="https://fatoora.zatca.gov.sa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-amber-900 underline font-medium mr-1"
                >
                  fatoora.zatca.gov.sa
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </li>
              <li>سجل الدخول بحسابك</li>
              <li>
                {isProductionEnv
                  ? 'اذهب إلى بيئة الإنتاج (Production) > توليد رمز OTP'
                  : 'اذهب إلى بيئة المحاكاة (Simulation) > توليد رمز OTP'}
              </li>
              <li>انسخ الرمز وأدخله أدناه</li>
            </ol>
          </div>

          <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3 text-xs text-amber-600">
            <AlertCircle className="w-4 h-4 inline ml-1" />
            {isProductionEnv
              ? 'استخدم OTP من بيئة الإنتاج فقط. أي OTP من Simulation لن يعمل هنا.'
              : 'استخدم OTP من بيئة Simulation فقط. أي OTP من Production لن يعمل هنا.'}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">رمز OTP</label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="أدخل الرمز المكون من 6 أرقام"
              maxLength={6}
              className="w-full px-6 py-4 border-2 border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-center text-2xl font-mono tracking-[0.5em]"
              dir="ltr"
              autoFocus
            />
          </div>

          <button
            onClick={handleRequestCompliance}
            disabled={processing || otp.length < 4}
            className="w-full px-6 py-3.5 bg-amber-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-amber-700 transition-colors disabled:opacity-50"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                جاري الربط مع ZATCA...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                ربط مع ZATCA
              </>
            )}
          </button>
        </motion.div>
      )}

      {/* ===== Step 4: Connected ===== */}
      {step === 4 && isConnected && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
          <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-200 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-emerald-800 mb-1">
              {zatcaStatus?.status === 'PRODUCTION_READY'
                ? (isProductionEnv ? 'متصل بالإنتاج الحقيقي! 🎉' : 'محاكاة الإنتاج مكتملة! ✅')
                : (isProductionEnv ? 'تم الربط الأولي بالإنتاج ✅' : 'تم الربط بالمحاكاة بنجاح! 🎉')}
            </h3>
            <p className="text-emerald-600 text-sm">
              {zatcaStatus?.status === 'PRODUCTION_READY'
                ? (isProductionEnv
                  ? 'نظامك جاهز الآن لإصدار الفواتير الإلكترونية على البيئة الحقيقية.'
                  : 'أكملت مسار Simulation بالكامل. الخطوة التالية: الانتقال إلى البيئة الحقيقية وربطها بـ OTP جديد.')
                : (isProductionEnv
                  ? 'تم إصدار Compliance CSID في الإنتاج. أكمل فحوصات الامتثال ثم اطلب Production CSID.'
                  : 'تم إصدار Compliance CSID في المحاكاة. أكمل فحوصات الامتثال ثم اطلب Production CSID (Simulation).')
              }
            </p>
          </div>

          {/* Details Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <span className="font-medium">تفاصيل الربط</span>
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-gray-500">الحالة:</span>
                    <span className="text-gray-800">{zatcaStatus?.status}</span>
                    <span className="text-gray-500">البيئة:</span>
                    <span className="text-gray-800">{zatcaStatus?.environment}</span>
                    <span className="text-gray-500">الرقم الضريبي:</span>
                    <span className="font-mono text-gray-800" dir="ltr">{profile.vatNumber}</span>
                    <span className="text-gray-500">اسم الشركة:</span>
                    <span className="text-gray-800">{profile.organizationName}</span>
                    {zatcaStatus?.onboardedAt && (
                      <>
                        <span className="text-gray-500">تاريخ الربط:</span>
                        <span className="text-gray-800" dir="ltr">
                          {new Date(zatcaStatus.onboardedAt).toLocaleDateString('ar-SA-u-ca-gregory')}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Compliance Checks + Production */}
          {zatcaStatus?.status === 'COMPLIANCE_DONE' && (
            <div className="border-t border-gray-100 pt-4 space-y-4">
              {/* Step A: Compliance Checks */}
              <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 text-sm text-amber-800">
                <p className="font-medium mb-1">📋 فحوصات الامتثال</p>
                <p>
                  يجب إرسال 6 فواتير امتثال في نفس البيئة الحالية ({isProductionEnv ? 'Production' : 'Simulation'})
                  قبل طلب Production CSID.
                </p>
              </div>

              {/* Compliance Results */}
              {complianceResults && (
                <div className={`rounded-xl p-4 text-sm space-y-2 ${complianceResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className="font-medium">{complianceResults.success ? '✅ جميع الفحوصات نجحت!' : `⚠️ نجح ${complianceResults.passed}/${complianceResults.total}`}</p>
                  <div className="space-y-1">
                    {complianceResults.results?.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        {r.success ? (
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                        <span className={r.success ? 'text-green-700' : 'text-red-600'}>{r.name}</span>
                        {r.error && <span className="text-red-400 text-xs">({r.error})</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleRunComplianceChecks}
                disabled={processing}
                className="w-full px-6 py-3 bg-amber-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    جاري تشغيل فحوصات الامتثال (6 فواتير)...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    {complianceResults?.success ? 'إعادة تشغيل الفحوصات' : 'تشغيل فحوصات الامتثال'}
                  </>
                )}
              </button>

              {/* Step B: Production - only show after compliance passes */}
              {complianceResults?.success && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="bg-emerald-50 p-3 rounded-xl text-sm text-emerald-700 mb-3">
                    <Zap className="w-4 h-4 inline ml-1" />
                    {isProductionEnv
                      ? 'جميع الفحوصات نجحت! يمكنك الآن طلب Production CSID النهائي.'
                      : 'جميع فحوصات Simulation نجحت! يمكنك الآن طلب Production CSID الخاص بالمحاكاة.'}
                  </div>
                  <button
                    onClick={handleRequestProduction}
                    disabled={processing}
                    className="w-full px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Zap className="w-5 h-5" />
                    )}
                    {isProductionEnv ? 'طلب شهادة الإنتاج النهائية' : 'طلب Production CSID (Simulation)'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Clear handoff guidance after completing simulation */}
          {!isProductionEnv && zatcaStatus?.status === 'PRODUCTION_READY' && (
            <div className="border-t border-gray-100 pt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 space-y-2">
                <p className="font-semibold">🚀 الانتقال إلى البيئة الحقيقية (Production)</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>غيّر متغير السيرفر: <span className="font-mono">ZATCA_ENV=production</span></li>
                  <li>أعد تشغيل الباك اند.</li>
                  <li>من نفس الصفحة اضغط "إعادة تعيين" لهذا الفرع فقط.</li>
                  <li>ابدأ الربط من جديد باستخدام OTP جديد من بوابة Production.</li>
                </ol>
                <p className="text-xs text-blue-600">
                  ملاحظة: OTP الخاص بالمحاكاة لا يعمل في الإنتاج.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

// ====== Main LabSettings Component ======
const LabSettings = () => {
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const { logoUrl: currentLogoUrl, fetchTenant } = useTenantBranding();
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [savingLogo, setSavingLogo] = useState(false);

  useEffect(() => {
    if (currentLogoUrl) setLogoPreview(currentLogoUrl);
  }, [currentLogoUrl]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('حجم الملف يجب أن لا يتجاوز 2MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('يجب اختيار ملف صورة'); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogoPreview(reader.result); setLogoBase64(reader.result); };
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = async () => {
    if (!logoBase64) { toast.error('لم يتم اختيار شعار جديد'); return; }
    setSavingLogo(true);
    try {
      await onboardingAPI.updateBranding({ logoUrl: logoBase64 });
      await fetchTenant();
      setLogoBase64(null);
      toast.success('تم تحديث شعار المختبر بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.message || 'تعذر حفظ الشعار');
    }
    setSavingLogo(false);
  };

  const handleRemoveLogo = async () => {
    setSavingLogo(true);
    try {
      await onboardingAPI.updateBranding({ logoUrl: '' });
      await fetchTenant();
      setLogoPreview(null);
      setLogoBase64(null);
      if (logoInputRef.current) logoInputRef.current.value = '';
      toast.success('تم حذف الشعار');
    } catch (error) {
      toast.error('تعذر حذف الشعار');
    }
    setSavingLogo(false);
  };

  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    lab_name: '',
    lab_name_en: '',
    vat_number: '',
    phone: '',
    invoice_footer_image: null,
    invoice_thank_you_message: '',
    invoice_thank_you_message_en: ''
  });

  const [previewImage, setPreviewImage] = useState(null);

  // WhatsApp Settings
  const [whatsappSettings, setWhatsappSettings] = useState({
    access_token: '',
    phone_number_id: '',
    is_enabled: false
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);

  // SMS Settings — Taqnyat (taqnyat.sa) is the only supported provider today.
  const [smsSettings, setSmsSettings] = useState({
    provider: 'taqnyat',
    is_enabled: false,
    taqnyat: { api_key: '', sender: '' },
  });
  const [showSmsKey, setShowSmsKey] = useState(false);
  const [savingSms, setSavingSms] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsBalance, setSmsBalance] = useState(null);
  const [loadingSmsBalance, setLoadingSmsBalance] = useState(false);

  useEffect(() => {
    const savedSettings = localStorage.getItem('labSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      if (parsed.invoice_footer_image) {
        setPreviewImage(parsed.invoice_footer_image);
      }
    }

    const loadWhatsappSettings = async () => {
      try {
        const response = await settingsAPI.getWhatsappSettings();
        const data = response.data?.data || response.data;
        setWhatsappSettings({
          access_token: data.access_token || '',
          phone_number_id: data.phone_number_id || '',
          is_enabled: data.is_enabled || false
        });
      } catch (err) {
        console.error('Failed to load WhatsApp settings:', err);
      }
    };

    const loadSmsSettings = async () => {
      try {
        const response = await settingsAPI.getSmsSettings();
        const data = response.data?.data || response.data;
        setSmsSettings({
          provider: data.provider || 'taqnyat',
          is_enabled: !!data.is_enabled,
          taqnyat: {
            api_key: data.taqnyat?.api_key || '',
            sender: data.taqnyat?.sender || '',
          },
        });
      } catch (err) {
        console.error('Failed to load SMS settings:', err);
      }
    };

    loadWhatsappSettings();
    loadSmsSettings();
  }, []);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('حجم الصورة يجب أن يكون أقل من 2 ميجابايت');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('يرجى اختيار ملف صورة');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setPreviewImage(base64);
      setSettings(prev => ({ ...prev, invoice_footer_image: base64 }));
      toast.success('تم تحميل الصورة بنجاح');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    setSettings(prev => ({ ...prev, invoice_footer_image: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
    toast.success('تم حذف الصورة');
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      localStorage.setItem('labSettings', JSON.stringify(settings));
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      toast.error('حدث خطأ أثناء حفظ الإعدادات');
    }
    setLoading(false);
  };

  const handleWhatsappInputChange = (field, value) => {
    setWhatsappSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveWhatsappSettings = async () => {
    setSavingWhatsapp(true);
    try {
      await settingsAPI.updateWhatsappSettings(whatsappSettings);
      toast.success('تم حفظ إعدادات الواتساب بنجاح');
    } catch (error) {
      toast.error('حدث خطأ أثناء حفظ إعدادات الواتساب');
    }
    setSavingWhatsapp(false);
  };

  // ─── SMS (Taqnyat) handlers ────────────────────────────────────────
  const handleSmsChange = (field, value) => {
    if (field === 'provider' || field === 'is_enabled') {
      setSmsSettings((prev) => ({ ...prev, [field]: value }));
    } else {
      setSmsSettings((prev) => ({ ...prev, taqnyat: { ...prev.taqnyat, [field]: value } }));
    }
  };

  const handleSaveSmsSettings = async () => {
    setSavingSms(true);
    try {
      const res = await settingsAPI.updateSmsSettings(smsSettings);
      const fresh = res.data?.data || res.data;
      if (fresh) {
        setSmsSettings({
          provider: fresh.provider || 'taqnyat',
          is_enabled: !!fresh.is_enabled,
          taqnyat: {
            api_key: fresh.taqnyat?.api_key || '',
            sender: fresh.taqnyat?.sender || '',
          },
        });
      }
      toast.success('تم حفظ إعدادات الرسائل');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'تعذّر حفظ إعدادات الرسائل');
    }
    setSavingSms(false);
  };

  const handleTestSms = async () => {
    if (!smsTestPhone.trim()) {
      toast.error('أدخل رقم جوال الاختبار');
      return;
    }
    setTestingSms(true);
    try {
      await settingsAPI.testSms({
        phone: smsTestPhone.trim(),
        message: 'رسالة اختبار من نظام LIS Techs — إعدادات الرسائل تعمل بنجاح ✓',
      });
      toast.success('تم إرسال رسالة الاختبار');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'تعذّر إرسال رسالة الاختبار');
    }
    setTestingSms(false);
  };

  const handleCheckSmsBalance = async () => {
    setLoadingSmsBalance(true);
    try {
      const res = await settingsAPI.smsBalance();
      const balance = res.data?.data?.balance ?? res.data?.balance;
      setSmsBalance(balance);
      toast.success('تم جلب الرصيد');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'تعذّر جلب الرصيد');
    }
    setLoadingSmsBalance(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <Settings className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إعدادات المختبر</h1>
              <p className="text-slate-500 text-sm mt-1">إدارة إعدادات المختبر والفواتير والربط مع ZATCA</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Lab Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                معلومات المختبر الأساسية
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">اسم المختبر (عربي)</label>
                <input
                  type="text"
                  value={settings.lab_name}
                  onChange={(e) => handleInputChange('lab_name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">اسم المختبر (إنجليزي)</label>
                <input
                  type="text"
                  value={settings.lab_name_en}
                  onChange={(e) => handleInputChange('lab_name_en', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الرقم الضريبي</label>
                <input
                  type="text"
                  value={settings.vat_number}
                  onChange={(e) => handleInputChange('vat_number', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">رقم الهاتف الموحد</label>
                <input
                  type="text"
                  value={settings.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  dir="ltr"
                />
              </div>
            </div>
          </motion.div>

          {/* Lab Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <Image className="w-5 h-5 text-emerald-600" />
                </div>
                شعار المختبر
                <span className="text-xs text-gray-500 font-normal">(يظهر في شريط التنقل والتقارير)</span>
              </h2>
            </div>
            <div className="p-6">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="lab-logo-upload"
              />

              {!logoPreview ? (
                <label
                  htmlFor="lab-logo-upload"
                  className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-all"
                >
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <p className="text-gray-600 font-medium">اضغط لرفع الشعار</p>
                  <p className="text-sm text-gray-400 mt-1">PNG, JPG حتى 2 ميجابايت</p>
                </label>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center p-4 border border-gray-200 rounded-2xl bg-gray-50">
                    <img
                      src={logoPreview}
                      alt="شعار المختبر"
                      className="max-h-24 w-auto object-contain"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="lab-logo-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      تغيير الشعار
                    </label>
                    <button
                      onClick={handleRemoveLogo}
                      disabled={savingLogo}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 rounded-xl text-red-600 hover:bg-red-50 transition-colors text-sm disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      حذف
                    </button>
                  </div>
                </div>
              )}

              {logoBase64 && (
                <button
                  onClick={handleSaveLogo}
                  disabled={savingLogo}
                  className="mt-4 w-full px-4 py-3 bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {savingLogo ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  حفظ الشعار
                </button>
              )}
            </div>
          </motion.div>

          {/* ===== ZATCA Integration (New Full Section) ===== */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden lg:row-span-2"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-xl">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                الربط مع ZATCA
                <span className="text-xs text-gray-400 font-normal">(الفوترة الإلكترونية)</span>
              </h2>
            </div>
            <div className="p-6">
              <ZatcaIntegration />
            </div>
          </motion.div>

          {/* Invoice Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                إعدادات الفاتورة
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">رسالة الشكر (عربي)</label>
                <input
                  type="text"
                  value={settings.invoice_thank_you_message}
                  onChange={(e) => handleInputChange('invoice_thank_you_message', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">رسالة الشكر (إنجليزي)</label>
                <input
                  type="text"
                  value={settings.invoice_thank_you_message_en}
                  onChange={(e) => handleInputChange('invoice_thank_you_message_en', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  dir="ltr"
                />
              </div>
            </div>
          </motion.div>

          {/* Invoice Footer Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden lg:col-span-2"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Image className="w-5 h-5 text-blue-600" />
                </div>
                صورة فوتر الفاتورة
                <span className="text-xs text-gray-500 font-normal">(ستظهر في أسفل الفاتورة)</span>
              </h2>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="footer-image-upload"
                />

                {!previewImage ? (
                  <label
                    htmlFor="footer-image-upload"
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all"
                  >
                    <Upload className="w-12 h-12 text-gray-400 mb-3" />
                    <p className="text-gray-600 font-medium">اضغط لرفع صورة</p>
                    <p className="text-sm text-gray-400 mt-1">PNG, JPG حتى 2 ميجابايت</p>
                  </label>
                ) : (
                  <div className="space-y-4">
                    <div className="relative border border-gray-200 rounded-2xl overflow-hidden">
                      <img
                        src={previewImage}
                        alt="Invoice Footer Preview"
                        className="w-full h-auto max-h-64 object-contain bg-gray-50"
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <button
                          onClick={() => window.open(previewImage, '_blank')}
                          className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 transition-colors"
                          title="عرض الصورة"
                        >
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={handleRemoveImage}
                          className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-colors"
                          title="حذف الصورة"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                    <label
                      htmlFor="footer-image-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      تغيير الصورة
                    </label>
                  </div>
                )}
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    الصورة المرفوعة ستظهر في أسفل كل فاتورة يتم طباعتها. يُفضل أن تكون الصورة بأبعاد عريضة (مثلاً 800x200 بكسل) للحصول على أفضل عرض.
                  </span>
                </p>
              </div>
            </div>
          </motion.div>

          {/* ─────────────── SMS Settings (Taqnyat) ─────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden lg:col-span-2"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                إعدادات الرسائل النصية (SMS)
                <span className="text-xs text-gray-500 font-normal">— مزوّد تقنيات taqnyat.sa</span>
              </h2>
            </div>
            <div className="p-6 space-y-5">
              {/* Enable toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsSettings.is_enabled}
                  onChange={(e) => handleSmsChange('is_enabled', e.target.checked)}
                  className="w-5 h-5 rounded accent-blue-600"
                />
                <span className="font-medium text-gray-700">تفعيل إرسال الرسائل النصية</span>
              </label>

              {/* Help box — step-by-step setup guide */}
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-[13px] text-blue-900 leading-relaxed space-y-2">
                <p className="font-bold text-blue-800">خطوات الربط مع تقنيات (taqnyat.sa)</p>
                <ol className="list-decimal ps-5 space-y-1.5">
                  <li>
                    أنشئ حساباً على{' '}
                    <a href="https://taqnyat.sa" target="_blank" rel="noreferrer" className="underline font-semibold">
                      taqnyat.sa
                    </a>
                    {' '}واطلب اعتماد اسم المرسل (Sender ID — حتى 11 حرف إنجليزي/أرقام).
                  </li>
                  <li>من لوحة تقنيات انسخ <strong>مفتاح API (App Key)</strong> والصقه في الحقل أدناه ثم احفظ.</li>
                  <li>
                    اضغط <strong>"إرسال اختبار"</strong> أول مرة. الرسالة لن تصل فعلياً،
                    لكن المحاولة ستُسجَّل في لوحة تقنيات.
                  </li>
                  <li>
                    ادخل على{' '}
                    <strong>لوحة تقنيات ← الإعدادات ← الأمان (IP Whitelist)</strong>{' '}
                    — ستجد عنوان سيرفرنا{' '}
                    <code className="inline-block bg-white px-2 py-0.5 rounded border border-blue-200 font-mono font-bold text-blue-700" dir="ltr">
                      3.64.227.14
                    </code>
                    {' '}— اضغط <strong>"تفعيل"</strong> بجانبه.
                  </li>
                  <li>بعد التفعيل، اضغط "فحص الرصيد" للتأكد من الربط، ثم أعد الاختبار — هذه المرة ستصل الرسالة.</li>
                </ol>
                <p className="text-[11px] text-blue-700 pt-1 border-t border-blue-200">
                  رصيد الرسائل يُدار في حسابك على تقنيات — نحن لا نفرض رسوماً إضافية.
                </p>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  مفتاح API (Bearer Token)
                </label>
                <div className="relative">
                  <input
                    type={showSmsKey ? 'text' : 'password'}
                    value={smsSettings.taqnyat.api_key}
                    onChange={(e) => handleSmsChange('api_key', e.target.value)}
                    placeholder="••••••••••••••••••••••"
                    dir="ltr"
                    className="w-full px-4 py-2.5 pl-11 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmsKey((v) => !v)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showSmsKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  يتم تشفير المفتاح قبل حفظه. اترك الحقل فارغاً لإبقاء المفتاح المحفوظ سابقاً.
                </p>
              </div>

              {/* Sender ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  اسم المرسل (Sender ID)
                </label>
                <input
                  type="text"
                  value={smsSettings.taqnyat.sender}
                  onChange={(e) => handleSmsChange('sender', e.target.value)}
                  placeholder="MyLab"
                  maxLength={11}
                  dir="ltr"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  1–11 حرف إنجليزي أو أرقام. يجب أن يكون معتمداً من تقنيات.
                </p>
              </div>

              {/* Action row */}
              <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleSaveSmsSettings}
                  disabled={savingSms}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  حفظ إعدادات الرسائل
                </button>
                <button
                  type="button"
                  onClick={handleCheckSmsBalance}
                  disabled={loadingSmsBalance || !smsSettings.is_enabled}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingSmsBalance ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  فحص الرصيد
                </button>
                {smsBalance !== null && (() => {
                  // Taqnyat returns the balance as a scalar or a nested object
                  // like `{ balance: 12.5, currency: 'SAR' }`. Normalise here
                  // so users see a readable number instead of [object Object].
                  let amount = smsBalance;
                  let currency = '';
                  if (typeof smsBalance === 'object' && smsBalance) {
                    amount = smsBalance.balance ?? smsBalance.amount ?? smsBalance.credits ?? smsBalance.value ?? '';
                    currency = smsBalance.currency || smsBalance.unit || '';
                  }
                  return (
                    <span className="text-sm text-gray-600">
                      الرصيد الحالي:{' '}
                      <strong className="text-blue-600">
                        {String(amount)}{currency ? ` ${currency}` : ''}
                      </strong>
                    </span>
                  );
                })()}
              </div>

              {/* Test send */}
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">إرسال رسالة اختبار</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="tel"
                    value={smsTestPhone}
                    onChange={(e) => setSmsTestPhone(e.target.value)}
                    placeholder="05xxxxxxxx أو 9665xxxxxxxx"
                    dir="ltr"
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  <button
                    type="button"
                    onClick={handleTestSms}
                    disabled={testingSms || !smsSettings.is_enabled}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
                  >
                    {testingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    إرسال اختبار
                  </button>
                </div>
                {!smsSettings.is_enabled && (
                  <p className="text-[11px] text-amber-600 mt-2">
                    فعّل الخدمة واحفظ الإعدادات قبل إرسال رسالة اختبار.
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* WhatsApp Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden lg:col-span-2"
          >
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-xl">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                إعدادات الواتساب
                <span className="text-xs text-gray-500 font-normal">(WhatsApp Cloud API - Meta)</span>
              </h2>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={whatsappSettings.is_enabled}
                    onChange={(e) => handleWhatsappInputChange('is_enabled', e.target.checked)}
                    className="w-5 h-5 text-green-600 rounded"
                  />
                  <span className="font-medium text-gray-700">تفعيل خدمة الواتساب</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="md:col-span-2">
                  <p className="text-sm text-green-700 mb-4">
                    للحصول على هذه البيانات، قم بإنشاء تطبيق في{' '}
                    <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                      Meta for Developers
                    </a>
                    {' '}وفعّل WhatsApp Business API
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Access Token
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={whatsappSettings.access_token}
                      onChange={(e) => handleWhatsappInputChange('access_token', e.target.value)}
                      placeholder="أدخل Access Token..."
                      className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number ID
                  </label>
                  <input
                    type="text"
                    value={whatsappSettings.phone_number_id}
                    onChange={(e) => handleWhatsappInputChange('phone_number_id', e.target.value)}
                    placeholder="أدخل Phone Number ID..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 bg-white"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700">
                <p className="flex items-start gap-2">
                  <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    المفاتيح محفوظة بشكل مشفر وآمن في قاعدة البيانات باستخدام تشفير AES-256. لا تشارك هذه المفاتيح مع أي شخص.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveWhatsappSettings}
                  disabled={savingWhatsapp}
                  className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {savingWhatsapp ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  حفظ إعدادات الواتساب
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 flex justify-center"
        >
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold text-lg flex items-center gap-3 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Save className="w-6 h-6" />
            )}
            حفظ الإعدادات
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default LabSettings;
